import { type MouseEvent, useCallback, useRef, useState } from 'react';

import { getRangeSlice, toggleObject } from '@/utils/helper-utils';

type ItemWithId = { id: string };

type UseRangeSelectionParams<T extends ItemWithId> = {
  items: T[];
  getSelections: () => T[];
  setSelections: (next: T[]) => void;
  /** When true (Archive), plain click toggles like Cmd/Ctrl. */
  toggleOnPlainClick?: boolean;
  onPlainClick?: (item: T) => void;
};

export const useRangeSelection = <T extends ItemWithId>(params: UseRangeSelectionParams<T>) => {
  const { items, getSelections, setSelections, toggleOnPlainClick = false, onPlainClick } = params;

  const [anchorId, setAnchorId] = useState<string | null>(null);
  const [lastShiftRangeIds, setLastShiftRangeIds] = useState<string[]>([]);

  const itemsRef = useRef(items);
  itemsRef.current = items;

  const lastShiftRangeIdsRef = useRef(lastShiftRangeIds);
  lastShiftRangeIdsRef.current = lastShiftRangeIds;

  const onPlainClickRef = useRef(onPlainClick);
  onPlainClickRef.current = onPlainClick;

  const resetRangeState = useCallback(() => {
    setAnchorId(null);
    setLastShiftRangeIds([]);
  }, []);

  const applyToggle = useCallback(
    (item: T) => {
      const selections = getSelections();
      const nextSelections = toggleObject(selections, item);
      const isSelectedAfterToggle = nextSelections.some((doc) => doc.id === item.id);

      setSelections(nextSelections);
      setLastShiftRangeIds([]);
      setAnchorId((prevAnchor) => {
        if (isSelectedAfterToggle) {
          return item.id;
        }
        if (prevAnchor === item.id) {
          return null;
        }
        return prevAnchor;
      });
    },
    [getSelections, setSelections]
  );

  const applyShift = useCallback(
    (item: T) => {
      const visibleItems = itemsRef.current;
      const selections = getSelections();
      const anchorInList = !!anchorId && visibleItems.some((doc) => doc.id === anchorId);

      if (!anchorInList) {
        setSelections([item]);
        setAnchorId(item.id);
        setLastShiftRangeIds([item.id]);
        return;
      }

      const range = getRangeSlice(visibleItems, anchorId!, item.id);
      if (!range) return;

      const previousShiftIds = lastShiftRangeIdsRef.current;
      const filtered = selections.filter((doc) => !previousShiftIds.includes(doc.id));
      const rangeIds = new Set(range.map((doc) => doc.id));
      const keep = filtered.filter((doc) => !rangeIds.has(doc.id));
      setSelections([...keep, ...range]);
      setLastShiftRangeIds(range.map((doc) => doc.id));
    },
    [anchorId, getSelections, setSelections]
  );

  const onItemClick = useCallback(
    (event: Pick<MouseEvent, 'shiftKey' | 'metaKey' | 'ctrlKey'>, item: T) => {
      if (event.shiftKey) {
        applyShift(item);
        return;
      }

      if (event.metaKey || event.ctrlKey || toggleOnPlainClick) {
        applyToggle(item);
        return;
      }

      resetRangeState();
      onPlainClickRef.current?.(item);
    },
    [applyShift, applyToggle, resetRangeState, toggleOnPlainClick]
  );

  return {
    onItemClick,
    toggleItem: applyToggle,
    resetRangeState,
  };
};
